import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatFCFA } from "@/lib/format";
import Modal, { TextField, TextArea, SelectField } from "@/components/Modal";
import { toast } from "sonner";

export default function InstallRequestModal({ product, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    address: "", zone: "", phone: user?.phone || "", description: "",
    urgency: "programmer", scheduled_date: "", scheduled_time: "",
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => { api.get("/zones").then((r) => setZones(r.data)); }, []);

  if (!user) {
    return (
      <Modal title="Connexion requise" onClose={onClose} testid="install-auth-modal">
        <p className="text-sm text-slate-600 mb-4">Vous devez être connecté pour demander une installation.</p>
        <button onClick={() => navigate("/login")} className="w-full bg-[#C85A32] text-white font-bold py-2.5 rounded-lg" data-testid="install-login-redirect">Se connecter</button>
      </Modal>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/installations", {
        product_id: product.id, product_name: product.name,
        skill: product.subcategory || "installation",
        address: f.address, zone: f.zone, phone: f.phone, description: f.description,
        urgency: f.urgency, scheduled_date: f.urgency === "programmer" ? f.scheduled_date : "",
        scheduled_time: f.urgency === "programmer" ? f.scheduled_time : "",
        client_amount: product.installation_price || 0,
      });
      toast.success("Demande d'installation envoyée");
      onClose();
      navigate("/mes-installations");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Demander l'installation" onClose={onClose} testid="install-modal">
      <div className="bg-[#FDF3EE] rounded-lg p-3 mb-4 text-sm">
        <p className="font-semibold text-slate-800">{product.name}</p>
        <p className="text-[#C85A32] font-num font-bold">Installation : {formatFCFA(product.installation_price)}</p>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <span className="text-sm font-semibold text-slate-700">Niveau d'urgence</span>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button type="button" onClick={() => set("urgency")("urgent")} data-testid="urgency-urgent"
              className={`py-2.5 rounded-lg text-sm font-bold border-2 ${f.urgency === "urgent" ? "border-[#DC2626] bg-red-50 text-[#DC2626]" : "border-slate-200 text-slate-500"}`}>⚡ Urgent</button>
            <button type="button" onClick={() => set("urgency")("programmer")} data-testid="urgency-programmer"
              className={`py-2.5 rounded-lg text-sm font-bold border-2 ${f.urgency === "programmer" ? "border-[#C85A32] bg-[#FDF3EE] text-[#C85A32]" : "border-slate-200 text-slate-500"}`}>📅 Programmer</button>
          </div>
        </div>
        {f.urgency === "programmer" && (
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Date souhaitée" type="date" value={f.scheduled_date} onChange={set("scheduled_date")} testid="install-date" required />
            <TextField label="Heure" type="time" value={f.scheduled_time} onChange={set("scheduled_time")} testid="install-time" required />
          </div>
        )}
        <SelectField label="Zone" value={f.zone} onChange={set("zone")} testid="install-zone" options={zones.map((z) => ({ value: z.name, label: z.name }))} />
        <TextField label="Adresse d'intervention" value={f.address} onChange={set("address")} required testid="install-address" />
        <TextField label="Téléphone de contact" value={f.phone} onChange={set("phone")} testid="install-phone" placeholder="+228 ..." />
        <TextArea label="Description (facultatif)" value={f.description} onChange={set("description")} testid="install-desc" />
        <button disabled={busy} className="w-full bg-[#0F172A] text-white font-bold py-3 rounded-lg disabled:opacity-50" data-testid="install-submit">
          {busy ? "Envoi..." : "Envoyer la demande"}
        </button>
      </form>
    </Modal>
  );
}
